import type { Organism } from '@verndale/core';

type ClientModuleDescriptor =
  | { type: 'class'; impl: new (el: HTMLElement, props?: Record<string, string>) => unknown }
  | { type: 'vanillaFn'; impl: (el: HTMLElement, dataset: DOMStringMap) => void }
  | { type: 'vanillaMount'; impl: (el: HTMLElement, dataset: DOMStringMap) => void };

function isEs6Class(fn: unknown): boolean {
  return typeof fn === 'function' && /^\s*class\s/.test(Function.prototype.toString.call(fn));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeClientModule(m: any): ClientModuleDescriptor {
  if (m.default != null && isEs6Class(m.default)) {
    return { type: 'class', impl: m.default };
  }

  if (typeof m.mount === 'function') {
    return { type: 'vanillaMount', impl: m.mount };
  }

  if (typeof m.default === 'function') {
    return { type: 'vanillaFn', impl: m.default };
  }

  throw new Error(
    'Client module must export one of: (1) default ES class, (2) mount(), (3) default function(el, dataset)'
  );
}

const mountedClientModuleNodes = new WeakSet<HTMLElement>();

function clientRender(mod: ClientModuleDescriptor, nodeList: NodeListOf<HTMLElement>) {
  const nodes = [...nodeList].filter(node => {
    if (mountedClientModuleNodes.has(node)) return false;

    mountedClientModuleNodes.add(node);
    return true;
  });

  switch (mod.type) {
    case 'class':
      nodes.forEach(node => {
        new mod.impl(node);
      });
      break;
    case 'vanillaFn':
    case 'vanillaMount':
      nodes.forEach(node => {
        mod.impl(node, node.dataset);
      });
      break;
    default: {
      const exhaustiveCheck: never = mod;
      return exhaustiveCheck;
    }
  }
}

const modules: Organism[] = [
  // GENERATED_CLIENT_MODULES_START
  {
    name: 'accordion',
    loader: () =>
      import('./modules/accordion').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'ai-detectors-listing',
    loader: () =>
      import('./modules/ai-detectors-listing').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'all-products-listing',
    loader: () =>
      import('./modules/all-products-listing').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'arc-flash-calculator',
    loader: () =>
      import('./modules/arc-flash-calculator').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'basic-image',
    loader: () =>
      import('./modules/basic-image').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'bento-section',
    loader: () =>
      import('./modules/bento-section').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'bundle-pricing-options-block',
    loader: () =>
      import('./modules/bundle-pricing-options-block').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'content-logos',
    loader: () =>
      import('./modules/content-logos').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'countdown-timer',
    loader: () =>
      import('./modules/countdown-timer').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'device-registry-listing',
    loader: () =>
      import('./modules/device-registry-listing').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'events-listing',
    loader: () =>
      import('./modules/events-listing').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'events-news-carousel',
    loader: () =>
      import('./modules/events-news-carousel').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'excentos-form',
    loader: () =>
      import('./modules/excentos-form').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'expertise-card-row',
    loader: () =>
      import('./modules/expertise-card-row').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'faq-block',
    loader: () =>
      import('./modules/faq-block').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'features-comparison-table',
    loader: () =>
      import('./modules/features-comparison-table').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'flip-card-grid',
    loader: () =>
      import('./modules/flip-card-grid').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'form-field',
    loader: () =>
      import('./modules/form-field').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'generic-card',
    loader: () =>
      import('./modules/generic-card').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'grouped-pricing-options-block',
    loader: () =>
      import('./modules/grouped-pricing-options-block').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'header',
    loader: () =>
      import('./modules/header').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'hero-banner',
    loader: () =>
      import('./modules/hero-banner').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'hotspot-image',
    loader: () =>
      import('./modules/hotspot-image').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'image-stack-banner',
    loader: () =>
      import('./modules/image-stack-banner').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'infinite-loop-infographic',
    loader: () =>
      import('./modules/infinite-loop-infographic').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'latest-news-card',
    loader: () =>
      import('./modules/latest-news-card').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'logo-carousel',
    loader: () =>
      import('./modules/logo-carousel').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'marketo-form-analytics',
    loader: () =>
      import('./modules/marketo-form-analytics').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'marketo-form-error-display',
    loader: () =>
      import('./modules/marketo-form-error-display').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'marketo-form-focus-rings',
    loader: () =>
      import('./modules/marketo-form-focus-rings').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'marketo-form-modal',
    loader: () =>
      import('./modules/marketo-form-modal').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'marketo-inline-form',
    loader: () =>
      import('./modules/marketo-inline-form').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'marketo-thank-you',
    loader: () =>
      import('./modules/marketo-thank-you').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'media-content-block',
    loader: () =>
      import('./modules/media-content-block').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'media-with-caption',
    loader: () =>
      import('./modules/media-with-caption').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'mini-cart',
    loader: () =>
      import('./modules/mini-cart').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'minimal-logo-carousel',
    loader: () =>
      import('./modules/minimal-logo-carousel').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'modal',
    loader: () =>
      import('./modules/modal').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'news-article-listing',
    loader: () =>
      import('./modules/news-article-listing').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'on-page-navigation',
    loader: () =>
      import('./modules/on-page-navigation').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'partner-listing',
    loader: () =>
      import('./modules/partner-listing').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'portfolio-listing',
    loader: () =>
      import('./modules/portfolio-listing').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'pricing-options-block',
    loader: () =>
      import('./modules/pricing-options-block').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'product-hero',
    loader: () =>
      import('./modules/product-hero').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'project-listing',
    loader: () =>
      import('./modules/project-listing').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'projectwise-bundle-flex-pricing-options-block',
    loader: () =>
      import('./modules/projectwise-bundle-flex-pricing-options-block').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'promotion-banner',
    loader: () =>
      import('./modules/promotion-banner').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'range-field',
    loader: () =>
      import('./modules/range-field').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'related-products-block',
    loader: () =>
      import('./modules/related-products-block').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'related-speaker-cards',
    loader: () =>
      import('./modules/related-speaker-cards').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'resource-listing',
    loader: () =>
      import('./modules/resource-listing').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'search-jobs',
    loader: () =>
      import('./modules/search-jobs').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'search-overlay',
    loader: () =>
      import('./modules/search-overlay').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'search-results',
    loader: () =>
      import('./modules/search-results').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'session-card',
    loader: () =>
      import('./modules/session-card').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'session-cards-block',
    loader: () =>
      import('./modules/session-cards-block').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'share-block',
    loader: () =>
      import('./modules/share-block').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'solution-area-products',
    loader: () =>
      import('./modules/solution-area-products').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'solutions-card',
    loader: () =>
      import('./modules/solutions-card').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'story-carousel',
    loader: () =>
      import('./modules/story-carousel').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'swatches-pricing-options-block',
    loader: () =>
      import('./modules/swatches-pricing-options-block').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'tab-pill-navigation',
    loader: () =>
      import('./modules/tab-pill-navigation').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'testimonial-carousel',
    loader: () =>
      import('./modules/testimonial-carousel').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'user-stories-carousel',
    loader: () =>
      import('./modules/user-stories-carousel').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  },
  {
    name: 'video-hero',
    loader: () =>
      import('./modules/video-hero').then((m: unknown) => ({
        default: normalizeClientModule(m)
      })),
    render: clientRender
  }
  // GENERATED_CLIENT_MODULES_END
];

export default modules;
